/**
 * BIRHAUS Healthcare Theme
 * 
 * Specialized theme for healthcare systems, medical records, and patient management.
 * 
 * Design principles:
 * - Calm and reassuring colors for patient comfort
 * - High contrast for medical accuracy and accessibility
 * - Clear visual hierarchy for critical health information
 * - Professional appearance for clinical settings
 */

import { BirhausThemeConfig } from './financial'

export const healthcareTheme: BirhausThemeConfig = {
  name: 'BIRHAUS Healthcare',
  version: '3.0.0',
  description: 'v3.0 Radical Minimalist calming theme for healthcare systems, medical records, and patient management',
  industry: 'healthcare',
  
  colors: {
    // Primary: Calming teal for healthcare trust
    primary: '#0891B2',
    primaryHover: '#0E7490',
    primaryDisabled: '#67E8F9',
    
    // Semantic colors for medical contexts
    success: '#059669',
    successBackground: '#ECFDF5',
    warning: '#D97706',
    warningBackground: '#FFFBEB',
    error: '#DC2626',
    errorBackground: '#FEF2F2',
    info: '#2563EB',
    infoBackground: '#EFF6FF',
    
    // Soft neutrals for patient comfort
    background: '#FFFFFF',
    surface: '#F0F9FF',
    surfaceElevated: '#FFFFFF',
    border: '#E0F2FE',
    borderSubtle: '#F0F9FF',
    
    // High contrast text for medical precision
    textPrimary: '#0C4A6E',
    textSecondary: '#0369A1',
    textDisabled: '#7DD3FC',
    textInverse: '#FFFFFF',
    
    // Healthcare-specific colors
    income: '#059669',     // Green for recovery/positive
    expense: '#DC2626',    // Red for critical/urgent
    transfer: '#7C3AED',   // Purple for referrals
    pending: '#D97706',    // Orange for pending results
    approved: '#059669',   // Green for approved treatment
    rejected: '#DC2626'    // Red for contraindicated
  },
  
  typography: {
    fontFamily: {
      sans: ['Inter', 'system-ui', '-apple-system', 'sans-serif'],
      mono: ['SF Mono', 'Consolas', 'Liberation Mono', 'monospace']
    },
    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem'
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.625
    }
  },
  
  spacing: {
    '0': '0',
    '1': '0.25rem',
    '2': '0.5rem',
    '3': '0.75rem',
    '4': '1rem',
    '5': '1.25rem',
    '6': '1.5rem',
    '8': '2rem',
    '10': '2.5rem',
    '12': '3rem',
    '16': '4rem',
    '20': '5rem',
    '24': '6rem',
    '32': '8rem'
  },
  
  borderRadius: {
    none: '0',
    sm: '0.125rem',
    base: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px'
  },
  
  boxShadow: {
    sm: '0 1px 2px 0 rgb(8 145 178 / 0.05)',
    base: '0 1px 3px 0 rgb(8 145 178 / 0.1), 0 1px 2px -1px rgb(8 145 178 / 0.1)',
    lg: '0 10px 15px -3px rgb(8 145 178 / 0.1), 0 4px 6px -4px rgb(8 145 178 / 0.1)',
    xl: '0 20px 25px -5px rgb(8 145 178 / 0.1), 0 8px 10px -6px rgb(8 145 178 / 0.1)'
  },
  
  // v3.0 Glass Morphism Configuration with healthcare calming colors
  glass: {
    primary: 'rgba(8, 145, 178, 0.15)',
    secondary: 'rgba(8, 145, 178, 0.08)',
    surface: 'rgba(240, 249, 255, 0.85)',
    border: 'rgba(224, 242, 254, 0.3)',
    blurIntensity: {
      light: 10,
      medium: 14,
      heavy: 26
    },
    saturation: 130,
    opacity: {
      subtle: 0.08,
      light: 0.22,
      medium: 0.42,
      heavy: 0.62,
      ultra: 0.75
    }
  },
  
  // v3.0 Generous Spacing (2x traditional)
  spacingV3: {
    none: '0px',
    xs: '8px',
    sm: '16px',
    md: '24px',
    lg: '32px',
    xl: '48px',
    '2xl': '64px',
    '3xl': '96px',
    cardPadding: '32px',
    buttonPadding: '16px 32px',
    inputPadding: '16px 20px',
    sectionMargin: '48px'
  },
  
  // v3.0 Radical Border Radius with clean healthcare feel
  borderRadiusV3: {
    xs: '6px',
    sm: '10px',
    md: '14px',
    lg: '22px',
    xl: '30px',
    '2xl': '38px',
    '3xl': '46px',
    circle: '50%',
    pill: '9999px',
    card: '22px',
    button: '14px',
    input: '10px',
    dialog: '30px'
  },
  
  // v3.0 Enhanced Shadows with Calming Healthcare Effects
  shadowV3: {
    none: 'none',
    sm: '0 1px 3px rgba(8, 145, 178, 0.06)',
    md: '0 4px 12px rgba(8, 145, 178, 0.06)',
    lg: '0 8px 24px rgba(8, 145, 178, 0.08)',
    xl: '0 16px 48px rgba(8, 145, 178, 0.1)',
    glass: '0 8px 32px rgba(8, 145, 178, 0.04)',
    floating: '0 12px 40px rgba(8, 145, 178, 0.12)',
    elevated: '0 20px 60px rgba(8, 145, 178, 0.15)',
    button: '0 4px 12px rgba(8, 145, 178, 0.08)',
    card: '0 8px 24px rgba(8, 145, 178, 0.06)',
    dialog: '0 24px 80px rgba(8, 145, 178, 0.18)',
    tooltip: '0 4px 16px rgba(8, 145, 178, 0.1)'
  },
  
  // v3.0 Calm Animations for Healthcare Context
  animationV3: {
    duration: {
      instant: '0ms',
      fast: '180ms',
      base: '320ms',
      slow: '480ms',
      slower: '720ms',
      slowest: '960ms'
    },
    easing: {
      linear: 'linear',
      smooth: 'cubic-bezier(0.25, 0.8, 0.25, 1)',
      snappy: 'cubic-bezier(0.4, 0, 0.2, 1)',
      bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
      gentle: 'cubic-bezier(0.25, 0.8, 0.25, 1)',
      entrance: 'cubic-bezier(0, 0, 0.2, 1)',
      exit: 'cubic-bezier(0.4, 0, 1, 1)'
    },
    useGPU: true,
    respectReducedMotion: true
  },
  
  components: {
    currency: {
      positive: '#059669',
      negative: '#DC2626',
      neutral: '#0C4A6E',
      fontSize: '1rem',
      fontFamily: 'SF Mono, Consolas, monospace',
      fontWeight: 600
    },
    
    statusBadge: {
      pending: { bg: '#FFFBEB', text: '#92400E', border: '#FCD34D' },
      approved: { bg: '#ECFDF5', text: '#065F46', border: '#34D399' },
      rejected: { bg: '#FEF2F2', text: '#991B1B', border: '#F87171' },
      transferred: { bg: '#EFF6FF', text: '#1E40AF', border: '#60A5FA' },
      received: { bg: '#F0FDF4', text: '#14532D', border: '#22C55E' }
    },
    
    dataTable: {
      headerBg: '#F0F9FF',
      headerText: '#0C4A6E',
      rowBg: '#FFFFFF',
      rowBgAlt: '#F8FAFC',
      rowBorder: '#E0F2FE',
      textAlign: 'left'
    },
    
    form: {
      inputBg: '#FFFFFF',
      inputBorder: '#BAE6FD',
      inputFocusBorder: '#0891B2',
      labelText: '#0C4A6E',
      errorText: '#DC2626',
      successText: '#059669'
    },
    
    card: {
      bg: '#FFFFFF',
      border: '#E0F2FE',
      shadow: '0 1px 3px 0 rgb(8 145 178 / 0.1)',
      headerBg: '#F0F9FF',
      padding: '1.5rem'
    }
  },
  
  labels: {
    es: {
      amount: 'Cantidad',
      balance: 'Balance',
      income: 'Ingreso',
      expense: 'Gasto',
      transfer: 'Transferencia',
      date: 'Fecha',
      status: 'Estado',
      pending: 'Pendiente',
      approved: 'Aprobado',
      rejected: 'Rechazado',
      total: 'Total',
      subtotal: 'Subtotal',
      tax: 'Impuesto',
      
      save: 'Guardar',
      cancel: 'Cancelar',
      edit: 'Editar',
      delete: 'Eliminar',
      approve: 'Aprobar',
      reject: 'Rechazar',
      transferAction: 'Transferir',
      export: 'Exportar',
      print: 'Imprimir',
      
      dashboard: 'Tablero',
      transactions: 'Transacciones',
      reports: 'Reportes',
      settings: 'Configuración',
      users: 'Usuarios',
      
      success: 'Éxito',
      error: 'Error',
      warning: 'Advertencia',
      loading: 'Cargando...',
      noData: 'No hay datos disponibles'
    }
  }
}

export default healthcareTheme